import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useCreate } from "@refinedev/core";
import { useBoundStore } from "@/store";
import { resetAllSlices } from "@/store/utils";
import { authService } from "@/services/authService";
import { useSystemConfiguration } from "./useSystemConfiguration";
import { Organization } from "types";
import i18n from "i18n";

/**
 * Auth Actions Hook
 * Provides all auth-related mutation actions
 *
 * Actions:
 * - login(): Login user (delegates to authProvider for now)
 * - logout(): Logout user and clear all state
 * - selectOrganization(): Switch to different organization
 * - createOrganization(): Create new organization with optimistic update
 * - setSidebarCollapsed(): Toggle sidebar visibility
 */
export const useAuthActions = () => {
  const queryClient = useQueryClient();

  // Zustand actions
  const selectOrganizationAction = useBoundStore(
    (state) => state.selectOrganization
  );
  const setSidebarCollapsedAction = useBoundStore(
    (state) => state.setSidebarCollapsed
  );
  const setSidebarHoveredAction = useBoundStore(
    (state) => state.setSidebarHovered
  );

  // Refine mutations
  const { mutateAsync: createOrgMutation, isPending: isCreatingOrg } =
    useCreate<Organization>();

  // System configuration
  const { supportedLanguages, isLoading: isLoadingLanguage } =
    useSystemConfiguration();

  /**
   * Login action (currently delegates to authProvider)
   * After login, queries will auto-fetch due to token presence
   */
  const login = useCallback(async () => {
    // Login is handled by authProvider.login()
    // After successful login, queries will automatically be enabled
    await queryClient.invalidateQueries({ queryKey: ["session"] });
  }, [queryClient]);

  /**
   * Logout action
   * Note: In practice, use Refine's useLogout().mutate() instead, which calls
   * authProvider.logout() and handles queryClient.clear() automatically.
   * This function is kept for compatibility but delegates to authService.
   */
  const logout = useCallback(async () => {
    // authProvider.logout() now handles queryClient.clear() automatically
    await authService.logout();
    resetAllSlices(); // Reset Zustand in-memory state
  }, []);

  /**
   * Select organization
   * Updates Zustand state and syncs to localStorage
   */
  const selectOrganization = useCallback(
    (id: string) => {
      selectOrganizationAction(id);
    },
    [selectOrganizationAction]
  );

  /**
   * Create organization with optimistic update
   * Immediately updates cache, then syncs with backend
   */
  const createOrganization = useCallback(
    async (name: string) => {
      if (isLoadingLanguage) return null;

      try {
        const newOrg = await createOrgMutation({
          resource: "media/organizations",
          values: {
            name,
            city: "",
            country: "",
            language:
              supportedLanguages.find((language) =>
                language.startsWith(i18n.language)
              ) ?? "en-US",
          },
          successNotification: false,
        });

        if (newOrg.data) {
          // Cancel any in-flight fetches so they don't overwrite our optimistic update
          await queryClient.cancelQueries({
            queryKey: ["session", "organizations"],
          });

          // Optimistic update: immediately update cache
          // Cache format is a plain array (from useOrganizationsQuery's queryFn)
          queryClient.setQueryData(
            ["session", "organizations"],
            (old: Organization[] | undefined) => [...(old ?? []), newOrg.data]
          );

          // Select the new organization
          selectOrganization(newOrg.data.id);

          // Sync with backend in the background (don't await).
          // Awaiting could overwrite the optimistic update with stale API data
          // if the backend hasn't propagated the new org yet (race condition),
          // which causes a redirect loop between /home and /organization/create.
          void queryClient.invalidateQueries({
            queryKey: ["session", "organizations"],
          });

          // Also refresh user data so accesses are updated for the new org
          void queryClient.invalidateQueries({
            queryKey: ["session", "user"],
          });
        }

        return newOrg;
      } catch (e) {
        console.error("Failed to create organization:", e);
        return null;
      }
    },
    [
      createOrgMutation,
      queryClient,
      selectOrganization,
      supportedLanguages,
      isLoadingLanguage,
    ]
  );

  /**
   * Set sidebar collapsed state
   */
  const setSidebarCollapsed = useCallback(
    (collapsed: boolean | null) => {
      setSidebarCollapsedAction(collapsed);
    },
    [setSidebarCollapsedAction]
  );

  /**
   * Set sidebar hovered state
   */
  const setSidebarHovered = useCallback(
    (hovered: boolean) => {
      setSidebarHoveredAction(hovered);
    },
    [setSidebarHoveredAction]
  );

  return {
    login,
    logout,
    selectOrganization,
    createOrganization,
    setSidebarCollapsed,
    setSidebarHovered,
    isCreatingOrg,
  };
};
